"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { parkSlug } from "@/lib/park-url";
// Types only. The library itself is loaded inside the init effect: a static
// import put maplibre's ~1 MB in the bundle of every route that renders a map,
// and Next's link prefetch fetched it on every page that merely LINKS to /map.
// Lighthouse counted 230 KB of it as unused JavaScript on the home page.
import type * as maplibregl from "maplibre-gl";
import { accessFilterClauses } from "@/lib/access-filters";
import "maplibre-gl/dist/maplibre-gl.css";
import { useRouter } from "next/navigation";

// Minimal HTML escape — popup body is built by string concatenation, so
// every interpolated value runs through this to avoid markup injection.
function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function ordinalString(n: number): string {
  const r = Math.round(n);
  const tens = r % 100;
  if (tens >= 11 && tens <= 13) return `${r}th`;
  switch (r % 10) {
    case 1: return `${r}st`;
    case 2: return `${r}nd`;
    case 3: return `${r}rd`;
    default: return `${r}th`;
  }
}

export type ParksMapProps = {
  geojson: GeoJSON.FeatureCollection & { mode?: "cache" | "sample" | "database" };
  highlightFeature?: GeoJSON.Feature | null;
  highlightId?: string;
  height?: string;
  filterMin?: number;
  filterMax?: number;
  minConfidence?: number;
  minCanopy?: number;
  minComfort?: number;
  minActivity?: number;
  hasActivityOnly?: boolean;
  requiredAmenities?: string[]; // park must have ALL of these
  typology?: string;            // legacy single-typology filter (kept for back-compat)
  typologies?: string[];        // multi-select typology filter — park must be in this list
  search?: string;
  // Access layer. Unlike the numeric filters above, a park with no mapped
  // access data is EXCLUDED while one of these is active rather than being
  // treated as a zero. The clauses are built by accessFilterClauses in
  // src/lib/access-filters.ts, which owns the matching JS predicate too, so
  // these no longer need to be kept in step by hand.
  minPavedPct?: number;
  noSteps?: boolean;
  maxWalkToSeatM?: number;
  parkingNearby?: boolean;
};

// Single source of truth for the Vitality Score colour ramp. The map
// paint expression and the page legend both derive from this so the two
// can never drift out of sync. Stop 100 is interpolation-only (the ramp
// keeps climbing past 75) and is excluded from the legend swatches.
export const SCORE_RAMP_STOPS: { value: number; colour: string; legend: boolean }[] = [
  { value: 0, colour: "#c1666b", legend: true },
  { value: 30, colour: "#e76f51", legend: true },
  { value: 45, colour: "#e9c46a", legend: true },
  { value: 60, colour: "#52b788", legend: true },
  { value: 75, colour: "#2d6a4f", legend: true },
  { value: 100, colour: "#1b4332", legend: false },
];

// Stops the legend should render as discrete swatches.
export const SCORE_LEGEND_STOPS: { value: number; colour: string }[] = SCORE_RAMP_STOPS
  .filter((s) => s.legend)
  .map(({ value, colour }) => ({ value, colour }));

const SCORE_COLOUR_RAMP: maplibregl.ExpressionSpecification = [
  "interpolate", ["linear"], ["coalesce", ["get", "overallScore"], 0],
  ...SCORE_RAMP_STOPS.flatMap((s) => [s.value, s.colour]),
] as maplibregl.ExpressionSpecification;

export function ParksMap({
  geojson,
  highlightFeature,
  highlightId,
  height = "70vh",
  filterMin = 0,
  filterMax = 100,
  minConfidence = 0,
  minCanopy = 0,
  minComfort = 0,
  minActivity = 0,
  hasActivityOnly = false,
  requiredAmenities = [],
  typology = "",
  typologies = [],
  minPavedPct = 0,
  noSteps = false,
  maxWalkToSeatM = 0,
  parkingNearby = false,
  search = "",
}: ParksMapProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const router = useRouter();

  // Lifecycle state for the loading / error surfaces. `status` starts in
  // "loading" and flips to "ready" on map.on("load"), or to "error" if the
  // basemap or tiles fail. `retryKey` is bumped by the retry button to
  // force the init effect to tear down and rebuild the map.
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [retryKey, setRetryKey] = useState(0);

  const retry = useCallback(() => {
    setStatus("loading");
    setRetryKey((k) => k + 1);
  }, []);

  // maplibre v5+ starts its worker from a URL it computes at runtime as a
  // sibling of `import.meta.url`. Under Turbopack that is the hashed chunk
  // URL, so it asks for /_next/static/chunks/maplibre-gl-worker.mjs, gets
  // Next's 404 page, and the browser refuses the HTML as a module script.
  //
  // The failure is silent: the map still constructs, acquires WebGL2 and
  // draws its controls, but the worker is dead, so no tiles or glyphs are
  // ever requested and `load` below never fires. The page sits on
  // "Loading map..." with nothing in the console pointing at the cause.
  //
  // scripts/copy-maplibre-worker.mjs copies the worker AND the shared module
  // it imports into public/ on predev and prebuild, so this URL resolves and
  // always matches the installed version. Set in the init effect below, once
  // the library has loaded.

  const isPoint = useMemo(
    () => geojson.features[0]?.geometry?.type === "Point",
    [geojson],
  );

  // Build the filter expression — common across point + polygon. Each
  // optional control becomes one extra clause. Amenity filter uses
  // index-of: a value of -1 means the type is missing.
  const filter = useMemo(() => {
    // Score-filter clauses are only emitted when the filter is actually
    // engaged. With filterMin=0 + filterMax=100 (defaults, no user input)
    // we don't add the `has` clause and missing-score parks stay on the
    // map — otherwise the result-strip "Showing X of Y" would silently
    // drop them. The JS list at MapClient.filtered mirrors this gate.
    const scoreActive = filterMin > 0 || filterMax < 100;
    const parts: maplibregl.FilterSpecification[] = [
      [">=", ["coalesce", ["get", "confidence"], 0], minConfidence],
      [">=", ["coalesce", ["get", "canopyPercent"], 0], minCanopy],
      [">=", ["coalesce", ["get", "naturalComfortScore"], 0], minComfort],
    ];
    if (scoreActive) {
      parts.push(["has", "overallScore"] as maplibregl.FilterSpecification);
      parts.push([">=", ["get", "overallScore"], filterMin] as maplibregl.FilterSpecification);
      parts.push(["<=", ["get", "overallScore"], filterMax] as maplibregl.FilterSpecification);
    }
    if (minActivity > 0) {
      parts.push([">=", ["coalesce", ["get", "activityScore"], 0], minActivity] as maplibregl.FilterSpecification);
    }
    if (hasActivityOnly) {
      // Park must have an activityScore — i.e. activity ETL ran for it.
      parts.push(["has", "activityScore"] as maplibregl.FilterSpecification);
    }
    for (const a of requiredAmenities) {
      parts.push([
        ">=",
        ["index-of", a, ["coalesce", ["get", "amenityTypes"], ["literal", []]]],
        0,
      ] as maplibregl.FilterSpecification);
    }
    if (typology) {
      parts.push(["==", ["coalesce", ["get", "typology"], ""], typology] as maplibregl.FilterSpecification);
    }
    if (typologies.length > 0) {
      // MapLibre: ["in", value, ["literal", arr]] returns true when value is
      // a member of arr. We coalesce to keep parks without a typology out
      // of every multi-select. Cap the array at 64 entries so a future
      // caller can't blow up MapLibre's expression evaluator by passing
      // thousands; the canonical caller sources from ALL_TYPOLOGIES (11
      // entries) so the cap is never hit in practice.
      const capped = typologies.slice(0, 64);
      parts.push([
        "in",
        ["coalesce", ["get", "typology"], ""],
        ["literal", capped],
      ] as maplibregl.FilterSpecification);
    }
    if (search.trim()) {
      const needle = search.trim().toLowerCase();
      parts.push([
        "in",
        needle,
        ["downcase", ["coalesce", ["get", "name"], ""]],
      ] as maplibregl.FilterSpecification);
    }
    // Access clauses come from src/lib/access-filters.ts, which also owns
    // the JS predicate MapClient.filtered uses. One module, one set of
    // sentinels, and test/access-filter-parity.test.ts asserts the two
    // forms agree, so the canvas and the card list cannot drift apart.
    for (const clause of accessFilterClauses({ minPavedPct, noSteps, maxWalkToSeatM, parkingNearby })) {
      parts.push(clause as maplibregl.FilterSpecification);
    }
    return ["all", ...parts] as maplibregl.FilterSpecification;
  }, [filterMin, filterMax, minConfidence, minCanopy, minComfort, minActivity, hasActivityOnly, requiredAmenities, typology, typologies, search, minPavedPct, noSteps, maxWalkToSeatM, parkingNearby]);

  useEffect(() => {
    if (!ref.current) return;
    const styleUrl =
      process.env.NEXT_PUBLIC_MAP_STYLE ?? "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";
    const center: [number, number] = [
      Number(process.env.NEXT_PUBLIC_MAP_CENTER_LON ?? -79.3832),
      Number(process.env.NEXT_PUBLIC_MAP_CENTER_LAT ?? 43.6532),
    ];
    const zoom = Number(process.env.NEXT_PUBLIC_MAP_ZOOM ?? 11);

    // The effect can be torn down (unmount, retry, a new highlight) before the
    // import resolves, so the map is only built if it is still wanted.
    let cancelled = false;
    let built: maplibregl.Map | null = null;
    import("maplibre-gl")
      .then((ml) => {
        if (cancelled || !ref.current) return;
        ml.setWorkerUrl("/maplibre-gl-worker.mjs");
        const map = new ml.Map({ container: ref.current, style: styleUrl, center, zoom });
        built = map;
        mapRef.current = map;
        map.addControl(new ml.NavigationControl({ showCompass: false }), "top-right");
        map.addControl(new ml.ScaleControl({ maxWidth: 80, unit: "metric" }), "bottom-left");

        // A failed basemap style or tile fetch leaves the user staring at a
        // bare div. Surface it: flip to the error panel so the retry path is
        // reachable. Errors after the map is "ready" (e.g. one stray tile)
        // are not promoted to a full-screen error so a working map stays up.
        map.on("error", () => {
          setStatus((s) => (s === "ready" ? s : "error"));
        });

        map.on("load", () => {
          setStatus("ready");
          map.addSource("parks", { type: "geojson", data: geojson as GeoJSON.GeoJSON });

          if (isPoint) {
            map.addLayer({
              id: "parks-circles",
              type: "circle",
              source: "parks",
              paint: {
                "circle-radius": [
                  "interpolate", ["linear"], ["zoom"],
                  9, 2.5,
                  12, 5,
                  15, 9,
                ],
                "circle-color": SCORE_COLOUR_RAMP,
                "circle-stroke-width": 0.6,
                "circle-stroke-color": "#264653",
                "circle-opacity": 0.85,
              },
              filter,
            });
          } else {
            map.addLayer({
              id: "parks-fill",
              type: "fill",
              source: "parks",
              paint: {
                "fill-color": SCORE_COLOUR_RAMP,
                "fill-opacity": ["case", ["==", ["get", "id"], highlightId ?? ""], 0.85, 0.55],
              },
              filter,
            });
            map.addLayer({
              id: "parks-outline",
              type: "line",
              source: "parks",
              paint: { "line-color": "#264653", "line-width": 1 },
            });
            map.addLayer({
              id: "parks-label",
              type: "symbol",
              source: "parks",
              layout: {
                "text-field": ["get", "name"],
                "text-size": 11,
                "text-offset": [0, 0.5],
                "text-anchor": "top",
              },
              paint: {
                "text-color": "#1b1b1b",
                "text-halo-color": "#ffffff",
                "text-halo-width": 1.2,
              },
            });
          }

          // Highlight (single-park polygon, e.g. on detail page)
          if (highlightFeature) {
            map.addSource("highlight", {
              type: "geojson",
              data: highlightFeature as GeoJSON.GeoJSON,
            });
            map.addLayer({
              id: "highlight-fill",
              type: "fill",
              source: "highlight",
              paint: { "fill-color": "#2d6a4f", "fill-opacity": 0.45 },
            });
            map.addLayer({
              id: "highlight-line",
              type: "line",
              source: "highlight",
              paint: { "line-color": "#1b4332", "line-width": 1.5 },
            });

            // Fit to highlight bounds
            const g = highlightFeature.geometry;
            const points: [number, number][] = [];
            if (g.type === "Polygon") {
              for (const ring of g.coordinates) for (const c of ring) points.push(c as [number, number]);
            } else if (g.type === "MultiPolygon") {
              for (const poly of g.coordinates) for (const ring of poly) for (const c of ring) points.push(c as [number, number]);
            } else if (g.type === "Point") {
              points.push(g.coordinates as [number, number]);
            }
            if (points.length) {
              const lons = points.map((p) => p[0]);
              const lats = points.map((p) => p[1]);
              map.fitBounds(
                [
                  [Math.min(...lons), Math.min(...lats)],
                  [Math.max(...lons), Math.max(...lats)],
                ],
                { padding: 80, maxZoom: 16, duration: 0 },
              );
            }
          }

          // Hover and tap both render the same summary. Two popups so a tap
          // preview is not blown away by an incidental mousemove; the hover
          // popup auto-clears on mouseleave, the click popup persists until
          // dismissed and carries an explicit "View park" navigation action.
          const hoverPopup = new ml.Popup({ closeButton: false, offset: 12 });
          const clickPopup = new ml.Popup({ closeButton: true, offset: 12 });
          const interactive = isPoint ? "parks-circles" : "parks-fill";

          // Builds the popup body. `withAction` adds a "View park" button —
          // used by the click/tap path so touch + keyboard users get a clear
          // navigation affordance instead of an immediate route change.
          function popupHTML(p: Record<string, unknown>, withAction: boolean): string {
            const typPct = p.typologyPercentile != null ? Number(p.typologyPercentile) : null;
            const label = (p.performanceLabel as string | null) ?? null;
            // Performance label tint, mapped to Material-3 role colours.
            const labelColour = label?.includes("over")
              ? "var(--md-sys-color-primary)"
              : label?.includes("under")
                ? "var(--md-sys-color-tertiary)"
                : "var(--md-sys-color-secondary)";
            // Park name + typology come from the build cache today, but
            // expert-review submissions / volunteer observations write
            // user-supplied text upstream. Escape any HTML so a future change
            // can't inject markup through the popup.
            const name = esc(String(p.name ?? ""));
            const typology = esc(String(p.typology ?? "typology"));
            const labelText = label ? esc(label) : "";
            // Guard the score: a park without an overallScore would render
            // "NaN / 100". Show the score line only when the value is real.
            const rawScore = p.overallScore;
            const scoreNum =
              rawScore == null || rawScore === "" ? NaN : Number(rawScore);
            const scoreLine = Number.isFinite(scoreNum)
              ? `<span>Vitality ${scoreNum.toFixed(0)} / 100</span>${
                  typPct !== null
                    ? `<span style="color:var(--md-sys-color-secondary)"> · ${ordinalString(typPct)} in ${typology}</span>`
                    : ""
                }<br/>`
              : `<span style="color:var(--md-sys-color-on-surface-variant)">Vitality score not yet available</span><br/>`;
            const confNum = Number(p.confidence);
            const confText = Number.isFinite(confNum)
              ? `${(confNum * 100).toFixed(0)}%`
              : "unknown";
            const id = esc(String(p.id ?? ""));
            // Canonical name-slug for the detail link + the data attribute the click
            // handler reads (router.push prepends /parks/), so the map navigates
            // straight to /parks/{name-slug}-{id} with no redirect hop.
            const slug = esc(parkSlug(String(p.name ?? ""), String(p.id ?? "")));
            return `<div style="font: 12px ui-sans-serif, system-ui; min-width: 200px; color: var(--md-sys-color-on-surface);">
                  <strong style="font-size: 13px;">${name}</strong><br/>
                  ${scoreLine}
                  ${label ? `<span style="display:inline-block; margin-top:4px; padding:1px 8px; border-radius:9999px; background:${labelColour}; color:var(--md-sys-color-on-primary); font-size: 10px; letter-spacing: 0.05em; text-transform: uppercase;">${labelText}</span>` : ""}
                  <div style="margin-top:4px; color:var(--md-sys-color-on-surface-variant)">${Number(p.amenityCount) || 0} amenities · confidence ${confText}</div>
                  ${
                    withAction && id
                      ? `<a href="/parks/${slug}" data-park-link="${slug}" style="display:inline-flex; align-items:center; margin-top:8px; padding:4px 12px; border-radius:9999px; background:var(--md-sys-color-primary); color:var(--md-sys-color-on-primary); font-size:11px; font-weight:600; text-decoration:none;">View park</a>`
                      : ""
                  }
                </div>`;
          }

          map.on("mousemove", interactive, (e) => {
            if (!e.features || !e.features[0]) return;
            map.getCanvas().style.cursor = "pointer";
            const p = e.features[0].properties as Record<string, unknown>;
            hoverPopup.setLngLat(e.lngLat).setHTML(popupHTML(p, false)).addTo(map);
          });
          map.on("mouseleave", interactive, () => {
            map.getCanvas().style.cursor = "";
            hoverPopup.remove();
          });
          // Click / tap: preview first. The popup carries a "View park" link
          // so touch + screen-reader users get the same summary hover users
          // see, and an explicit, deliberate navigation step.
          map.on("click", interactive, (e) => {
            const f = e.features?.[0];
            if (!f) return;
            hoverPopup.remove();
            const p = f.properties as Record<string, unknown>;
            const node = document.createElement("div");
            node.innerHTML = popupHTML(p, true);
            const link = node.querySelector<HTMLAnchorElement>("a[data-park-link]");
            if (link) {
              link.addEventListener("click", (ev) => {
                ev.preventDefault();
                clickPopup.remove();
                router.push(`/parks/${link.dataset.parkLink}`);
              });
            }
            clickPopup.setLngLat(e.lngLat).setDOMContent(node).addTo(map);
          });
        });
      })
      .catch(() => {
        // A failed chunk load is the same outcome as a failed basemap: offer
        // the retry rather than leaving the skeleton up forever.
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
      built?.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Include highlightFeature in deps so swapping the highlight without
    // changing highlightId rebuilds the map. The effect's cleanup calls
    // map.remove(), so this is a full teardown + reinitialise — heavier
    // than ideal but safe; if it shows up in real-world latency, narrow
    // the update to `map.getSource('highlight').setData(...)` instead.
    // retryKey is in the deps so the retry button forces a full
    // teardown + reinitialise of the map after a load failure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geojson, highlightId, highlightFeature, retryKey]);

  // Update filter live without rebuilding the map
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    const layer = isPoint ? "parks-circles" : "parks-fill";
    if (map.getLayer(layer)) {
      map.setFilter(layer, filter);
    }
  }, [filter, isPoint]);

  return (
    <div
      className="relative rounded-lg overflow-hidden border border-border"
      style={{ width: "100%", height }}
    >
      <div
        ref={ref}
        role="application"
        aria-label="Interactive map of Toronto parks coloured by Jacobs Vitality Score. Hover or tap a park for a summary."
        aria-busy={status === "loading"}
        style={{ width: "100%", height: "100%" }}
      />

      {/* Loading skeleton — covers the bare div until map.on("load")
          fires so tiles arriving late don't read as an empty box. */}
      {status === "loading" && (
        <div
          aria-busy="true"
          aria-live="polite"
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-surface-container-low"
        >
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-outline-variant border-t-primary" />
          <p className="font-label-caps text-label-caps text-on-surface-variant">
            Loading map…
          </p>
          <span className="sr-only">The parks map is loading.</span>
        </div>
      )}

      {/* Error panel — basemap style or tiles failed to load. Carries an
          explicit retry that tears down and rebuilds the map. */}
      {status === "error" && (
        <div
          role="alert"
          className="absolute inset-0 flex items-center justify-center p-6 bg-surface-container-low"
        >
          <div className="max-w-sm rounded-xl border border-outline-variant/60 bg-surface-container-lowest p-6 text-center">
            <h3 className="font-headline-md text-headline-md text-on-surface">
              The map could not load
            </h3>
            <p className="mt-2 font-body-md text-body-md text-on-surface-variant">
              The basemap or park tiles failed to load. This is usually a
              network hiccup. Check your connection and try again.
            </p>
            <button
              type="button"
              onClick={retry}
              className="mt-4 inline-flex h-10 items-center gap-1.5 rounded-full bg-primary px-5 font-label-caps text-label-caps text-on-primary hover:bg-primary-container focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Retry
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
